/**
 * New registrations, at the top of the volunteers screen (Plan D12).
 *
 * **Read-only, and that is the whole design.** This panel shows what a person
 * actually submitted — the record §3.12 asks us to keep — and offers no way to
 * change it. Every action lives below, on the volunteer, because that is the one
 * surface that covers everybody: a volunteer entered by staff has no
 * registration behind them at all, and a decision screen that cannot handle them
 * is not the decision screen.
 *
 * It is a work list, not an archive. A submission leaves as soon as its
 * volunteer stops being `PENDING`, whichever screen made that happen, so an
 * empty panel means nothing is waiting. Everything ever submitted stays at
 * `/registrations`.
 *
 * Flagged possible duplicates sort first, because they are the ones that must be
 * looked at before somebody activates them out of habit.
 */

import { Link } from 'react-router-dom';
import type { RegistrationAwaitingDecision } from '@parinaam-vms/shared';
import { Card, DataTable, EmptyState, ErrorState, LoadingState } from '../../components/common';
import { useAwaitingDecision } from '../../hooks/useRegistrations';
import { formatDate, orDash } from '../../utils/format';
import styles from './SubmissionsPanel.module.css';

/**
 * Whether this submission resembles a *different* person.
 *
 * A repeat submission is flagged against the volunteer it reused, so its
 * `possible_duplicate_of` points at its own `created_volunteer_id`. Counting
 * that as a duplicate would mark someone as a possible duplicate of themselves
 * — the "sent 2×" badge is what that case actually deserves.
 */
function matchesSomebodyElse(row: RegistrationAwaitingDecision): boolean {
  return (
    Boolean(row.possible_duplicate_of) && row.possible_duplicate_of !== row.created_volunteer_id
  );
}

/**
 * One row per person, not per submission.
 *
 * Someone who submits the form twice produces two registrations pointing at one
 * `PENDING` volunteer (Plan D12 reuses the record rather than creating a second).
 * Listing both would show the same person twice and count them twice, so a panel
 * headed "6 people are waiting" would read "8" — and staff would open the same
 * record from two rows.
 *
 * The rows arrive flagged-first, so the representative kept here is the one
 * carrying the duplicate flag when there is one. The repeat count travels with
 * it, because "she has sent this three times" is worth knowing.
 */
function onePerVolunteer(rows: RegistrationAwaitingDecision[]) {
  const byVolunteer = new Map<string, { row: RegistrationAwaitingDecision; submissions: number }>();

  for (const row of rows) {
    const seen = byVolunteer.get(row.created_volunteer_id);
    if (seen) {
      seen.submissions += 1;
    } else {
      byVolunteer.set(row.created_volunteer_id, { row, submissions: 1 });
    }
  }

  return [...byVolunteer.values()];
}

export function SubmissionsPanel() {
  const query = useAwaitingDecision();
  const people = onePerVolunteer(query.data ?? []);
  const rows = people.map((entry) => entry.row);
  const submissionsFor = new Map(
    people.map((entry) => [entry.row.created_volunteer_id, entry.submissions])
  );
  const flagged = rows.filter(matchesSomebodyElse).length;

  return (
    <Card
      title={`New registrations${rows.length > 0 ? ` (${rows.length})` : ''}`}
      action={
        flagged > 0 && (
          <span className={styles.flagSummary}>
            {flagged} possible duplicate{flagged === 1 ? '' : 's'}
          </span>
        )
      }
    >
      {query.isPending ? (
        <LoadingState label="Loading new registrations…" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing waiting"
          message="Public registrations, waiting for a decision."
        />
      ) : (
        <>
          <p className={styles.intro}>
          </p>

          <DataTable<RegistrationAwaitingDecision>
            caption="Registrations awaiting a decision"
            rows={rows}
            getRowKey={(row) => row.registration_request_id}
            columns={[
              {
                key: 'name',
                header: 'Name',
                render: (row) => {
                  const submissions = submissionsFor.get(row.created_volunteer_id) ?? 1;

                  return (
                    <span className={styles.nameCell}>
                      {[row.first_name, row.last_name].filter(Boolean).join(' ')}
                      {matchesSomebodyElse(row) && (
                        <span className={styles.flag} title="Matches an existing volunteer">
                          Possible duplicate
                        </span>
                      )}
                      {submissions > 1 && (
                        <span className={styles.repeat} title="Submitted the form more than once">
                          sent {submissions}×
                        </span>
                      )}
                    </span>
                  );
                },
              },
              {
                key: 'contact',
                header: 'Contact',
                render: (row) => orDash(row.email ?? row.phone),
              },
              { key: 'city', header: 'City', render: (row) => orDash(row.city) },
              {
                key: 'submitted',
                header: 'Submitted',
                render: (row) => formatDate(row.submitted_at),
              },
              {
                key: 'duplicate',
                header: 'Matches',
                render: (row) =>
                  matchesSomebodyElse(row) ? (
                    // Named, not just marked: staff need to open the other record
                    // to judge whether it is the same person or a household.
                    <Link to={`/volunteers/${row.possible_duplicate_of}`}>
                      {row.duplicate_of_name} ({row.duplicate_of_code})
                    </Link>
                  ) : (
                    '—'
                  ),
              },
              {
                key: 'action',
                header: '',
                render: (row) => (
                  <span className={styles.actions}>
                    <Link to={`/volunteers/${row.created_volunteer_id}`}>Review →</Link>
                    <Link
                      to={`/registrations/${row.registration_request_id}`}
                      className={styles.secondaryLink}
                    >
                      {row.registration_code}
                    </Link>
                  </span>
                ),
              },
            ]}
          />
        </>
      )}
    </Card>
  );
}
